import { type ForwardRefExoticComponent, createElement, forwardRef } from 'react'

import { type GradientConfig, ICON_GRADIENTS } from '@ps/ds/icons/constants/gradients'
import { type IconNode, type LucideProps } from 'lucide-react'

import { getGradientUrl, renderGradientDef, resolveGradient } from './renderGradientDef'

export interface DuoToneIconProps extends LucideProps {
  /** Tailwind class for background */
  backgroundClassName?: string
  /** Tailwind class for foreground */
  foregroundClassName?: string
  /** Gradient for background (overrides backgroundClassName) */
  gradient?: GradientConfig | keyof typeof ICON_GRADIENTS
}

interface DuoToneIconOptions {
  /** Default background class */
  defaultBackgroundClassName?: string
  /** Default foreground class */
  defaultForegroundClassName?: string
  /** Default gradient */
  defaultGradient?: GradientConfig | keyof typeof ICON_GRADIENTS
  /** Index of first foreground path */
  foregroundStartIndex?: number
}

/**
 * Creates a two-tone icon with separate background/foreground styling.
 * Supports Tailwind classes and gradients.
 * @param iconName - Name of the icon component
 * @param iconNode - Lucide icon node definition
 * @param options - Configuration options for default classes, gradients, and foreground index
 *
 * @example
 * ```tsx
 * const Icon = createDuoToneIcon('MyIcon', iconNode, {
 *   defaultBackgroundClassName: 'fill-navy-500',
 *   foregroundStartIndex: 1,
 * })
 *
 * <Icon backgroundClassName="fill-blue-500" />
 * <Icon gradient="blue-green" foregroundClassName="fill-white" />
 * ```
 */
export function createDuoToneIcon(
  iconName: string,
  iconNode: IconNode,
  options: DuoToneIconOptions = {},
): ForwardRefExoticComponent<DuoToneIconProps> {
  const {
    defaultBackgroundClassName = 'fill-current',
    defaultForegroundClassName = 'fill-white',
    defaultGradient,
    foregroundStartIndex = 1,
  } = options

  const DuoToneIcon = forwardRef<SVGSVGElement, DuoToneIconProps>(
    (
      {
        backgroundClassName = defaultBackgroundClassName,
        foregroundClassName = defaultForegroundClassName,
        gradient,
        className,
        size = 24,
        strokeWidth = 2,
        ...props
      },
      ref,
    ) => {
      // Priority: explicit gradient > custom backgroundClassName > defaultGradient
      let effectiveGradient = gradient
      if (effectiveGradient === undefined && backgroundClassName === defaultBackgroundClassName) {
        effectiveGradient = defaultGradient
      }
      const gradientConfig = resolveGradient(effectiveGradient, ICON_GRADIENTS)

      // Render SVG elements directly without creating intermediate components
      const children = iconNode.map(([tag, attrs], index) => {
        const isForeground = index >= foregroundStartIndex
        const key = attrs.key ?? `${iconName}-${index}`

        // Build final attributes for this path
        const finalAttrs = {
          ...attrs,
          key,
          // Foreground always uses className
          ...(isForeground
            ? { className: foregroundClassName }
            : // Background uses gradient or className
              gradientConfig
              ? { fill: getGradientUrl(iconName, gradientConfig.id) }
              : { className: backgroundClassName }),
        }

        return createElement(tag, finalAttrs)
      })

      return (
        <>
          {renderGradientDef(iconName, gradientConfig)}
          <svg
            ref={ref}
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className ? `lucide ${className}` : 'lucide'}
            {...props}>
            {children}
          </svg>
        </>
      )
    },
  )

  DuoToneIcon.displayName = iconName

  return DuoToneIcon
}
